<template>
  <div>
    <div class="pagination-toolbar d-flex p-1 flex-row flex-wrap align-items-center">
      <div class="pagination-toolbar__controls d-flex justify-content-center flex-row align-items-center mb-2 mb-md-0">
        <div class="mr-2">
          <b-pagination
            v-model="params.page"
            :total-rows="totalRecords"
            :per-page="params.per_page"
            :limit="9"
            align="right"
            pills
            size="sm"
            class="my-0"
            first-number
            last-number
            @input="getTableData"
          >
            <template #next-text>
              <chevron-right-icon size="1x" class="custom-class"></chevron-right-icon>
            </template>
            <template #prev-text>
              <chevron-left-icon size="1x" class="custom-class"></chevron-left-icon>
            </template>
            <template #first-text><span class="text-success">First</span></template>
            <template #last-text><span class="text-info">Last</span></template>
          </b-pagination>
        </div>
        <div class="ml-2">
          <div class="d-flex flex-row justify-content-end">
            <b-form-input
              @keyup.enter="goToPage()"
              v-model="localMovingPage"
              type="text"
              class="mr-2 go-to-btn justify-content-end"
              @focus="$event.target.select()"
            ></b-form-input>
            <b-button variant="primary" @click="goToPage()">go</b-button>
          </div>
        </div>
      </div>
      <div class="pagination-toolbar__summary d-flex align-items-center mb-2 mb-md-0">
        <span class="text-black" v-if="totalRecords"><span class="font-weight-bold" > {{ totalRecords }}</span> Items</span>
        <v-select
          :options="paginationOptions.perPageDropdown"
          v-model="params.per_page"
          @input="getTableData"
          :clearable="false"
          :searchable="false"
        >
        </v-select>
      </div>
    </div>
  </div>
</template>

<script>
import { ChevronRightIcon, ChevronLeftIcon } from "vue-feather-icons"
import "vue-select/dist/vue-select.css"

export default {
  name: "pagination-controls",
  props: ["paginationOptions", "totalRecords", "movingPage", "rows", "params"],
  components: { ChevronRightIcon, ChevronLeftIcon },
  data() {
    return {
      // Local mirror of the `movingPage` prop for the "go to page" input. Vue warns
      // when a prop is mutated directly, so the input binds to this and we keep it in
      // sync with the prop via the watcher below.
      localMovingPage: this.movingPage,
      // Set when the user paginates; consumed once the new page data renders so the
      // page can be scrolled back to the top (see the `rows` watcher below).
      pendingScrollToTop: false
    }
  },
  watch: {
    movingPage(val) {
      this.localMovingPage = val
    },
    // The page data loads asynchronously, so a scroll fired on click gets interrupted
    // by the loading-overlay reflow and strands the page partway down. Re-pin to the
    // top once the new rows have actually rendered — reliable regardless of how long
    // the API takes.
    rows() {
      if (this.pendingScrollToTop) {
        this.pendingScrollToTop = false
        this.$nextTick(() => this.scrollToTop())
      }
    }
  },
  methods: {
    getTableData() {
      this.localMovingPage = this.params.page
      this.pendingScrollToTop = true
      this.$emit("get-table-data", this.params.page)
      this.scrollToTop()
      // b-pagination re-focuses the clicked page button on the next tick, and the
      // browser's scroll-into-view for that focus drags the scroller back down,
      // undoing the scroll above. Macrotask timers run after that focus, so re-pin
      // then (and once more in case the loading overlay reflows the layout).
      setTimeout(() => this.scrollToTop(), 0)
      setTimeout(() => this.scrollToTop(), 250)
    },
    goToPage() {
      this.params.page = this.localMovingPage
      this.getTableData()
    },
    // The admin layout sometimes scrolls on #layout-content (overflow-y: auto) and
    // sometimes on the document itself, and which one is scrollable can't be guessed
    // reliably (sub-pixel rounding and display scaling make scrollHeight checks lie).
    // Resetting a non-scrollable element is a harmless no-op, so pin every candidate.
    // Instant (not smooth): a smooth scroll started here gets interrupted by the
    // async data-load reflow and stalls partway down; an instant scroll holds.
    scrollToTop() {
      const candidates = [
        document.getElementById("layout-content"),
        document.scrollingElement || document.documentElement,
        document.body
      ]
      candidates.forEach(el => {
        if (el) {
          try {
            el.scrollTop = 0
          } catch (e) {
            // no-op — element not scrollable
          }
        }
      })
      window.scrollTo(0, 0)
    }
  }
}
</script>

<style scoped>
::v-deep .v-select div div input.vs__search {
  border: none !important;
}

.pl-35 {
  padding-left: 1.1rem !important;
}

.pagination-toolbar {
  gap: 0.75rem;
  justify-content: flex-start;
}

.pagination-toolbar__summary {
  gap: 0.25rem;
  margin-left: 1.25rem;
}

.pagination-toolbar__summary ::v-deep .vs__dropdown-menu {
  z-index: 2000;
}

/* When the per-page dropdown opens, vue-select normally sets the selected value
   to position:absolute + opacity:.4, which (combined with the global
   `.v-select { min-width: fit-content }`) shrinks the box and fades the number.
   Keep it in normal flow at full opacity so the box and number stay stable. */
.pagination-toolbar__summary ::v-deep .vs--single.vs--open .vs__selected {
  position: static;
  opacity: 1;
}
</style>
